import React, { useState, useRef, useEffect } from "react";
import { Globe, ChevronDown, Check } from "lucide-react";
import { useTranslation } from "react-i18next";

const LanguageSwitcher = ({ mobile = false }) => {
  const { i18n, t } = useTranslation("common");

  const [isOpen, setIsOpen] = useState(false);

  const dropdownRef = useRef(null);

  // Available languages
  const languages = [
    {
      code: "de-CH",
      name: t("language.german"),
      flag: "🇨🇭",
    },
    {
      code: "en",
      name: t("language.english"),
      flag: "🇬🇧",
    },
  ];

  // Get currently selected language
  const currentLanguage =
    languages.find((language) =>
      i18n.language?.startsWith(language.code)
    ) || languages[0];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
    };
  }, []);

  // Change language
  const changeLanguage = async (languageCode) => {
    try {
      await i18n.changeLanguage(languageCode);

      localStorage.setItem(
        "crypsyn-language",
        languageCode
      );

      setIsOpen(false);
    } catch (error) {
      console.error("Failed to change language:", error);
    }
  };

  return (
    <div
      ref={dropdownRef}
      className={`relative ${mobile ? "w-full" : ""}`}
    >
      {/* =========================================
          LANGUAGE BUTTON
      ========================================= */}

      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={`
          flex
          items-center
          justify-between
          gap-2
          rounded-xl
          border
          border-gray-200
          bg-white
          px-3
          py-2
          text-sm
          font-medium
          text-gray-700
          transition-all
          duration-200
          hover:border-blue-300
          hover:bg-blue-50
          hover:text-blue-600
          ${mobile ? "w-full px-4 py-3" : ""}
        `}
      >
        <span className="flex items-center gap-2">
          {/* Globe */}
          <Globe size={17} />

          {/* Flag */}
          <span>
            {currentLanguage.flag}
          </span>

          {/* Current language */}
          <span>
            {currentLanguage.name}
          </span>
        </span>

        {/* Arrow */}
        <ChevronDown
          size={16}
          className={`
            transition-transform
            duration-200
            ${isOpen ? "rotate-180" : ""}
          `}
        />
      </button>

      {/* =========================================
          DROPDOWN
      ========================================= */}

      {isOpen && (
        <div
          className={`
            absolute
            z-[100]
            mt-2
            w-48
            overflow-hidden
            rounded-2xl
            border
            border-gray-200
            bg-white
            p-2
            shadow-xl
            ${mobile ? "left-0 w-full" : "right-0"}
          `}
        >
          {/* Dropdown label */}

          <p className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
            {t("language.label")}
          </p>

          {/* Languages */}

          {languages.map((language) => {
            const isActive = i18n.language?.startsWith(
              language.code
            );

            return (
              <button
                key={language.code}
                type="button"
                onClick={() =>
                  changeLanguage(language.code)
                }
                className={`
                  flex
                  w-full
                  items-center
                  justify-between
                  rounded-xl
                  px-3
                  py-2.5
                  text-sm
                  transition-all
                  duration-200
                  ${
                    isActive
                      ? "bg-blue-50 text-blue-600"
                      : "text-gray-700 hover:bg-gray-50"
                  }
                `}
              >
                {/* Language name */}

                <span className="flex items-center gap-3">
                  <span className="text-lg">
                    {language.flag}
                  </span>

                  <span>
                    {language.name}
                  </span>
                </span>

                {/* Active check */}

                {isActive && (
                  <Check
                    size={16}
                    className="text-blue-600"
                  />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;